import { OrderHoldStatus, OrderHoldSubtype, OrderHoldType } from '@invenco/common-domain/enums';
import {
  FulfillmentStatus,
  OrderLineStatus,
  type OrderHoldDTO,
  OrderProcessStatusDTO,
  OrderProcessTypeDTO,
  type OrderDTO,
} from '@invenco/common-interface/sales';
import { ShipmentStatus, type ShipmentDTO } from '@invenco/shipping-interface';

import type { TimelineType } from '@/components/timeline';

export type OrderTimelineCategory =
  | 'order'
  | 'address'
  | 'inventory'
  | 'automation'
  | 'fulfillment'
  | 'shipment';

export type OrderTimelineSectionMarker = 'UPCOMING' | 'IN-PROGRESS' | 'COMPLETED';

export type OrderTimelineEntry = {
  id: string;
  category: OrderTimelineCategory;
  section: OrderTimelineSectionMarker;
  type: TimelineType;
  title: string;
  timestamp: string;
  description?: string;
};

export type OrderTimelineSection = {
  marker: OrderTimelineSectionMarker;
  entries: OrderTimelineEntry[];
};

type BuildOrderTimelineInput = {
  order?: OrderDTO;
  shipments?: ShipmentDTO[];
};

type OrderTimelineEntryInput = Omit<OrderTimelineEntry, 'timestamp'> & {
  timestamp?: string | null;
};
type TimelineState = Pick<OrderTimelineEntry, 'section' | 'type'>;

const SECTION_ORDER: OrderTimelineSectionMarker[] = ['UPCOMING', 'IN-PROGRESS', 'COMPLETED'];
const HOLD_TITLES: Partial<Record<OrderHoldType, string>> = {
  [OrderHoldType.AUTOMATION_PROCESS]: 'Automation failed',
  [OrderHoldType.RESERVE_INVENTORY_PROCESS]: 'Placed on backorder',
  [OrderHoldType.SHIPPING_ADDRESS_PROCESS]: 'Address validation failed',
  [OrderHoldType.ITEM_NOT_FOUND]: 'Item not found',
  [OrderHoldType.SHIPPING_HOLD]: 'Shipping hold',
};

function addEntry(entries: OrderTimelineEntry[], entry: OrderTimelineEntryInput | undefined) {
  if (entry?.timestamp) {
    entries.push({ ...entry, timestamp: entry.timestamp });
  }
}

function getProcess(order: OrderDTO, type: OrderProcessTypeDTO) {
  return order.orderProcesses?.find((process) => process.type === type);
}

function getProcessStartTimestamp(process: NonNullable<OrderDTO['orderProcesses']>[number]) {
  return process.lastStartedAt ?? process.createdAt;
}

function getOrderEventDescription(order: OrderDTO, verb: string) {
  if (order.channelName) return `${order.name} ${verb} via ${order.channelName}`;

  return `${order.name} ${verb}`;
}

function getBackorderDescription(order: OrderDTO) {
  const backorderLines =
    order.orderLines?.filter((line) => line.status === OrderLineStatus.BACKORDER) ?? [];

  if (backorderLines.length === 0) return 'Unable to fully reserve inventory';
  if (backorderLines.length > 1) {
    return `${backorderLines.length} items on backorder`;
  }

  return `${backorderLines[0].skuName} is on backorder`;
}

function getInvalidSkuDescription(order: OrderDTO) {
  const invalidLines =
    order.orderLines?.filter((line) => line.status === OrderLineStatus.ITEM_NOT_FOUND) ?? [];

  if (invalidLines.length === 0) return 'One or more SKUs are invalid';
  if (invalidLines.length > 1) {
    return `${invalidLines.length} invalid SKU names`;
  }

  return `${invalidLines[0].skuName} is an invalid SKU name`;
}

function getHoldDescription(order: OrderDTO, hold: OrderHoldDTO) {
  if (hold.description) return hold.description;

  if (hold.subtype) {
    switch (hold.subtype) {
      case OrderHoldSubtype.NO_PREFERRED_RATE:
        return `Cannot determine a preferred carrier service for shipping profile ${order.shippingProfileName}`;
      case OrderHoldSubtype.SHIPPING_PROFILE_NOT_FOUND:
        return `Unable to find a shipping profile${
          order.shippingDescription ? ` for description "${order.shippingDescription}"` : ''
        }`;
      case OrderHoldSubtype.MISSING_CUSTOMS_DATA:
        return 'Missing required customs data for international shipping';
    }
  }

  switch (hold.type) {
    case OrderHoldType.SHIPPING_ADDRESS_PROCESS:
      return 'Invalid customer address';
    case OrderHoldType.RESERVE_INVENTORY_PROCESS:
      return getBackorderDescription(order);
    case OrderHoldType.AUTOMATION_PROCESS:
      return 'Automation process failed';
    case OrderHoldType.ITEM_NOT_FOUND:
      return getInvalidSkuDescription(order);
    case OrderHoldType.MANUAL_HOLD:
      return 'Manually placed on hold';
    case OrderHoldType.SHIPPING_HOLD:
      return 'Error in shipping';
  }

  return undefined;
}

function itemCount(count: number) {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

function hasActiveBackorderHold(order: OrderDTO) {
  return order.orderHolds?.some(
    (hold) =>
      hold.type === OrderHoldType.RESERVE_INVENTORY_PROCESS && hold.status === OrderHoldStatus.HOLD,
  );
}

function getInventoryAllocatedDescription(order: OrderDTO) {
  const reserved = order.qtyReserved ?? 0;
  const committed = order.qtyCommitted ?? 0;
  const firstSentence =
    reserved === committed
      ? `${itemCount(reserved)} reserved and committed from stock`
      : `${itemCount(reserved)} reserved, ${itemCount(committed)} committed from stock`;

  if (committed < reserved && !hasActiveBackorderHold(order)) {
    return `${firstSentence}. ${itemCount(reserved - committed)} remain uncommitted.`;
  }

  return firstSentence;
}

function getFulfillmentLocationDescription(
  fulfillment: NonNullable<OrderDTO['fulfillments']>[number],
  prefix: string,
) {
  const locationName = fulfillment.warehouseLocationName ?? fulfillment.locationName;

  return locationName ? `${prefix} ${locationName}` : undefined;
}

function getFulfillmentStartedTimelineState(
  fulfillment: NonNullable<OrderDTO['fulfillments']>[number],
): TimelineState {
  if (fulfillment.status === FulfillmentStatus.PENDING) {
    return { section: 'IN-PROGRESS', type: 'progress' };
  }

  return { section: 'COMPLETED', type: 'success' };
}

function isFulfillmentOpen(fulfillment: NonNullable<OrderDTO['fulfillments']>[number]) {
  return fulfillment.status === FulfillmentStatus.OPEN;
}

function getFulfillmentBaseTitle(fulfillmentIndex: number, fulfillmentCount: number) {
  if (fulfillmentCount > 1) return `Fulfillment ${fulfillmentIndex + 1}`;

  return 'Fulfillment';
}

function getFulfillmentCompletionEntry(
  fulfillment: NonNullable<OrderDTO['fulfillments']>[number],
  fulfillmentTitle: string,
): Pick<OrderTimelineEntry, 'title' | 'type'> | undefined {
  if (fulfillment.status === FulfillmentStatus.CLOSED) {
    return { title: `${fulfillmentTitle} closed`, type: 'success' };
  }

  if (fulfillment.status === FulfillmentStatus.CANCELLED) {
    return { title: `${fulfillmentTitle} cancelled`, type: 'inactive' };
  }

  return undefined;
}

function getHoldTimelineState(hold: OrderHoldDTO): TimelineState {
  if (hold.status === OrderHoldStatus.HOLD) {
    return { section: 'IN-PROGRESS', type: 'alert' };
  }

  return { section: 'COMPLETED', type: 'inactive' };
}

function getHoldTitle(hold: OrderHoldDTO, action: 'applied' | 'released') {
  if (hold.type === OrderHoldType.MANUAL_HOLD) return `Manual hold ${action}`;

  return HOLD_TITLES[hold.type] ?? hold.type;
}

function getShipmentCreatedTimelineState(shipment: ShipmentDTO): TimelineState {
  if (shipment.status === ShipmentStatus.DRAFT || shipment.status === ShipmentStatus.WAITING) {
    return { section: 'UPCOMING', type: 'observe' };
  }

  return { section: 'COMPLETED', type: 'success' };
}

function getShipmentBaseTitle(shipment: ShipmentDTO, shipmentCount: number) {
  if (shipmentCount > 1 && shipment.number) return `Shipment ${shipment.number}`;

  return 'Shipment';
}

function getCarrierDescription(prefix: string, shipment: ShipmentDTO) {
  if (shipment.carrierName && shipment.carrierServiceName) {
    return `${prefix}: ${shipment.carrierName} (${shipment.carrierServiceName})`;
  }

  if (shipment.carrierName) return `${prefix}: ${shipment.carrierName}`;
  if (shipment.carrierServiceName) return `${prefix}: ${shipment.carrierServiceName}`;

  return undefined;
}

export function buildOrderTimeline({ order, shipments = [] }: BuildOrderTimelineInput) {
  const entries: OrderTimelineEntry[] = [];

  if (!order) return [];

  if (order.orderedAt !== order.createdAt) {
    addEntry(entries, {
      id: 'order-placed',
      category: 'order',
      section: 'COMPLETED',
      type: 'success',
      title: 'Order placed',
      timestamp: order.orderedAt,
      description: getOrderEventDescription(order, 'placed'),
    });
  }
  addEntry(entries, {
    id: 'order-created',
    category: 'order',
    section: 'COMPLETED',
    type: 'success',
    title: 'Order created',
    timestamp: order.createdAt,
    description: getOrderEventDescription(order, 'created'),
  });

  const addressProcess = getProcess(order, OrderProcessTypeDTO.SHIPPING_ADDRESS_VALIDATION);
  if (addressProcess) {
    if (addressProcess.status === OrderProcessStatusDTO.PENDING) {
      addEntry(entries, {
        id: `${addressProcess.id}-started`,
        category: 'address',
        section: 'IN-PROGRESS',
        type: 'progress',
        title: 'Address validation started',
        timestamp: getProcessStartTimestamp(addressProcess),
      });
    }
    if (addressProcess.status === OrderProcessStatusDTO.COMPLETE) {
      addEntry(entries, {
        id: `${addressProcess.id}-confirmed`,
        category: 'address',
        section: 'COMPLETED',
        type: 'success',
        title: 'Address confirmed',
        timestamp: addressProcess.updatedAt,
        description: 'Shipping address validated successfully',
      });
    }
  }

  const inventoryProcess = getProcess(order, OrderProcessTypeDTO.RESERVE_INVENTORY);
  if (inventoryProcess) {
    if (inventoryProcess.status === OrderProcessStatusDTO.PENDING) {
      addEntry(entries, {
        id: `${inventoryProcess.id}-started`,
        category: 'inventory',
        section: 'IN-PROGRESS',
        type: 'progress',
        title: 'Inventory reservation started',
        timestamp: getProcessStartTimestamp(inventoryProcess),
        description: `${itemCount(order.qtyBalance ?? 0)} requested for reservation`,
      });
    }
    if (inventoryProcess.status === OrderProcessStatusDTO.COMPLETE) {
      addEntry(entries, {
        id: `${inventoryProcess.id}-allocated`,
        category: 'inventory',
        section: 'COMPLETED',
        type: 'success',
        title: 'Inventory reserved',
        timestamp: inventoryProcess.updatedAt,
        description: getInventoryAllocatedDescription(order),
      });
    }
  }

  const automationProcess = getProcess(order, OrderProcessTypeDTO.AUTOMATION);
  if (automationProcess) {
    if (automationProcess.status === OrderProcessStatusDTO.PENDING) {
      addEntry(entries, {
        id: `${automationProcess.id}-started`,
        category: 'automation',
        section: 'IN-PROGRESS',
        type: 'progress',
        title: 'Automation started',
        timestamp: getProcessStartTimestamp(automationProcess),
      });
    }
    if (automationProcess.status === OrderProcessStatusDTO.COMPLETE) {
      addEntry(entries, {
        id: `${automationProcess.id}-applied`,
        category: 'automation',
        section: 'COMPLETED',
        type: 'success',
        title: 'Automation applied',
        timestamp: automationProcess.updatedAt,
        description: 'Any matching workflows ran successfully',
      });
    }
  }

  order.orderHolds?.forEach((hold) => {
    if (hold.type !== OrderHoldType.MANUAL_HOLD && hold.releasedAt) return;

    const { section, type } = getHoldTimelineState(hold);

    addEntry(entries, {
      id: `${hold.id}-applied`,
      category: 'order',
      section,
      type,
      title: getHoldTitle(hold, 'applied'),
      timestamp: hold.createdAt,
      description: getHoldDescription(order, hold),
    });
    if (hold.type === OrderHoldType.MANUAL_HOLD) {
      addEntry(entries, {
        id: `${hold.id}-released`,
        category: 'order',
        section: 'COMPLETED',
        type: 'success',
        title: getHoldTitle(hold, 'released'),
        timestamp: hold.releasedAt,
        description: getHoldDescription(order, hold),
      });
    }
  });

  order.fulfillments?.forEach((fulfillment, fulfillmentIndex) => {
    const fulfillmentTitle = getFulfillmentBaseTitle(
      fulfillmentIndex,
      order.fulfillments?.length ?? 0,
    );
    const fulfillmentLocationDescription = getFulfillmentLocationDescription(
      fulfillment,
      'Preparing items for shipment from',
    );

    if (!fulfillment.allocatedAt) {
      const fulfillmentStartedState = getFulfillmentStartedTimelineState(fulfillment);

      addEntry(entries, {
        id: `${fulfillment.id}-started`,
        category: 'fulfillment',
        section: fulfillmentStartedState.section,
        type: fulfillmentStartedState.type,
        title: `${fulfillmentTitle} started`,
        timestamp: fulfillment.createdAt,
        description: fulfillmentLocationDescription,
      });
    }
    if (isFulfillmentOpen(fulfillment)) {
      addEntry(entries, {
        id: `${fulfillment.id}-in-progress`,
        category: 'fulfillment',
        section: 'IN-PROGRESS',
        type: 'progress',
        title: fulfillmentTitle,
        timestamp: fulfillment.updatedAt,
        description: fulfillmentLocationDescription,
      });
    }
    addEntry(entries, {
      id: `${fulfillment.id}-allocated`,
      category: 'fulfillment',
      section: 'COMPLETED',
      type: 'success',
      title: `${fulfillmentTitle} allocated`,
      timestamp: fulfillment.allocatedAt,
      description: `${itemCount(fulfillment.qtyAllocated ?? 0)} allocated from stock`,
    });

    const fulfillmentCompletionEntry = getFulfillmentCompletionEntry(fulfillment, fulfillmentTitle);
    if (fulfillmentCompletionEntry) {
      addEntry(entries, {
        id: `${fulfillment.id}-${fulfillment.status.toLowerCase()}`,
        category: 'fulfillment',
        section: 'COMPLETED',
        type: fulfillmentCompletionEntry.type,
        title: fulfillmentCompletionEntry.title,
        // REST only gives the latest update timestamp, so this is our best available completion time.
        timestamp: fulfillment.updatedAt,
        description: fulfillmentLocationDescription,
      });
    }
  });

  shipments.forEach((shipment) => {
    const { section, type } = getShipmentCreatedTimelineState(shipment);
    const shipmentTitle = getShipmentBaseTitle(shipment, shipments.length);

    addEntry(entries, {
      id: `${shipment.id}-created`,
      category: 'shipment',
      section,
      type,
      title: `${shipmentTitle} created`,
      timestamp: shipment.createdAt,
    });
    if (shipment.status === ShipmentStatus.PROCESSING) {
      addEntry(entries, {
        id: `${shipment.id}-in-progress`,
        category: 'shipment',
        section: 'IN-PROGRESS',
        type: 'progress',
        title: shipmentTitle,
        timestamp: shipment.updatedAt,
        description: getCarrierDescription('Delivery by', shipment),
      });
    }
    if (shipment.status === ShipmentStatus.SHIPPED) {
      addEntry(entries, {
        id: `${shipment.id}-shipped`,
        category: 'shipment',
        section: 'COMPLETED',
        type: 'success',
        title: `${shipmentTitle} shipped`,
        timestamp: shipment.shippedAt ?? undefined,
        description: getCarrierDescription('Collected by', shipment),
      });
    }
    if (shipment.status === ShipmentStatus.CANCELLED) {
      addEntry(entries, {
        id: `${shipment.id}-cancelled`,
        category: 'shipment',
        section: 'COMPLETED',
        type: 'inactive',
        title: `${shipmentTitle} cancelled`,
        // REST only gives the latest update timestamp, so this is our best available cancellation time.
        timestamp: shipment.updatedAt,
      });
    }
  });

  const entriesBySection = entries.reduce<Record<OrderTimelineSectionMarker, OrderTimelineEntry[]>>(
    (sections, entry) => {
      sections[entry.section].push(entry);
      return sections;
    },
    { UPCOMING: [], 'IN-PROGRESS': [], COMPLETED: [] },
  );

  return SECTION_ORDER.map((marker) => ({
    marker,
    entries: entriesBySection[marker].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
  })).filter((section) => section.entries.length > 0);
}
